import {
  Button,
  IconButton,
  List,
  ListItem,
  Flex,
  Input,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  VStack,
  HStack,
  Text,
  Box,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiFolder,
  FiMoreVertical,
  FiFolderMinus,
  FiMessageSquare,
  FiCpu,
  FiColumns,
  FiImage,
  FiVideo,
  FiMusic,
  FiDatabase,
  FiVoicemail,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useChatStore } from "../../stores/chatStore";
import { useState, useEffect } from "react";
import { ChatSession, ChatSessionType } from "types";
import { MdSlideshow } from "react-icons/md";
import { FaCubes, FaMicrophone, FaUser } from "react-icons/fa";

interface ChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  sessions: any[];
  handleNewChat: (sessionType: ChatSessionType) => void;
  handleSelectSession: (id: string) => void;
  handleRename: (e: React.FormEvent<HTMLFormElement>) => void;
  handleDeleteSession: (
    id: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => void;
  editingSessionId: string | null;
  sessionNameInput: string;
  setSessionNameInput: (value: string) => void;
  useColorModeValue: (light: string, dark: string) => string;
  setEditingSessionId: (id: string | null) => void;
  handleStartRename: (
    session: any,
    e: React.MouseEvent<HTMLButtonElement>
  ) => void;
  currentSession: ChatSession | null;
}

interface ChatItemProps {
  chatSession: any;
  folders?: any[];
  handleSelectSession: (id: string) => void;
  handleRename: (e: React.FormEvent<HTMLFormElement>) => void;
  handleDeleteSession: (
    id: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => void;
  handleStartRename: (
    session: any,
    e: React.MouseEvent<HTMLButtonElement>
  ) => void;
  editingSessionId: string | null;
  sessionNameInput: string;
  setSessionNameInput: (value: string) => void;
  setEditingSessionId: (id: string | null) => void;
  removeChatFromFolder: (id: string) => void;
  addChatToFolder: (folderId: string, id: string) => Promise<void>;
  useColorModeValue: (light: string, dark: string) => string;
  t: (key: string, options?: any) => string;
  currentSession: ChatSession | null;
}

const ChatItem = ({
  chatSession,
  folders,
  handleSelectSession,
  handleRename,
  handleDeleteSession,
  handleStartRename,
  editingSessionId,
  sessionNameInput,
  setSessionNameInput,
  setEditingSessionId,
  removeChatFromFolder,
  addChatToFolder,
  useColorModeValue,
  t,
  currentSession,
}: ChatItemProps) => {
  const hoverBg = useColorModeValue("gray.100", "gray.600");
  const hoverColor = useColorModeValue("gray.800", "gray.200");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const isInFolder =
    Boolean(chatSession.folderId) ||
    Boolean(
      chatSession.id &&
        (folders || []).some((folder: { chatSessions: { id: string }[] }) =>
          folder.chatSessions.some(
            (chatItem: { id: string }) => chatItem.id === chatSession.id
          )
        )
    );
  const folderBg = useColorModeValue("gray.50", "gray.800");

  return (
    <ListItem
      _hover={{
        bg: hoverBg,
        color: hoverColor,
      }}
      cursor="pointer"
      borderBottom="1px"
      borderColor={borderColor}
      bg={
        currentSession?.id === chatSession.id
          ? "gray.600"
          : isInFolder
          ? folderBg
          : "transparent"
      }
      fontWeight={currentSession?.id === chatSession.id ? "bold" : "normal"}
      onClick={(e) => {
        e.stopPropagation();
        handleSelectSession(chatSession.id);
      }}
    >
      <Flex
        p={4}
        alignItems="center"
        justifyContent="space-between"
        pl={isInFolder ? 6 : 4}
      >
        <Flex align="center" flex={1}>
          <Box color="gray.500" mr={3}>
            {chatSession.sessionType === ChatSessionType.COMPARATIVE_CHAT ? (
              <FiColumns />
            ) : chatSession.sessionType === ChatSessionType.STANDARD ? (
              <FiMessageSquare />
            ) : chatSession.sessionType === ChatSessionType.IMAGE_CHAT ? (
              <FiImage />
            ) : chatSession.sessionType === ChatSessionType.AUDIO_CHAT ? (
              <FaMicrophone />
            ) : chatSession.sessionType === ChatSessionType.VIDEO_CHAT ? (
              <FiVideo />
            ) : chatSession.sessionType === ChatSessionType.PRESENTATION ? (
              <MdSlideshow />
            ) : chatSession.sessionType === ChatSessionType.DATA_MINING ? (
              <FiDatabase />
            ) : chatSession.sessionType === ChatSessionType.CHARACTER_CHAT ? (
              <FaUser />
            ) : chatSession.sessionType === ChatSessionType.MUSIC_CHAT ? (
              <FiMusic />
            ) : chatSession.sessionType === ChatSessionType.AGENT_CHAT ? (
              <FaCubes />
            ) : (
              <FiCpu />
            )}
          </Box>
          {editingSessionId === chatSession.id ? (
            <form
              onSubmit={(e) => handleRename(e)}
              style={{ flex: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Input
                value={sessionNameInput}
                onChange={(e) => setSessionNameInput(e.target.value)}
                placeholder={t("chat.sessionNamePlaceholder")}
                size="sm"
                autoFocus
                onBlur={() => setEditingSessionId(null)}
              />
            </form>
          ) : (
            <Text noOfLines={1}>
              {chatSession.sessionName || t("chat.newChat")}
            </Text>
          )}
        </Flex>
        <HStack spacing={1}>
          <Tooltip label={t("chat.renameSession")} placement="top">
            <IconButton
              aria-label={t("chat.renameSession")}
              icon={<FiEdit />}
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleStartRename({ id: chatSession.id }, e);
              }}
            />
          </Tooltip>
          <Tooltip label={t("chat.deleteSession")} placement="top">
            <IconButton
              aria-label={t("chat.deleteSession")}
              icon={<FiTrash2 />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSession(chatSession.id, e);
              }}
            />
          </Tooltip>
          {isInFolder ? (
            <Tooltip
              label={t("common.folders.removeFromFolder")}
              placement="top"
            >
              <IconButton
                aria-label={t("common.folders.removeFromFolder")}
                icon={<FiFolderMinus />}
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  removeChatFromFolder(chatSession.id);
                }}
              />
            </Tooltip>
          ) : folders && folders.length > 0 ? (
            <Menu>
              <Tooltip
                label={t("common.folders.moveTo", { name: "..." })}
                placement="top"
              >
                <MenuButton
                  as={IconButton}
                  aria-label={t("common.folders.moveTo", { name: "..." })}
                  icon={<FiMoreVertical />}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
              <MenuList onClick={(e) => e.stopPropagation()}>
                {folders.map((folder) => (
                  <MenuItem
                    key={folder.id}
                    icon={<FiFolder />}
                    onClick={() => addChatToFolder(folder.id, chatSession.id)}
                  >
                    {t("common.folders.moveTo", { name: folder.name })}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : null}
        </HStack>
      </Flex>
    </ListItem>
  );
};

const FolderItem = ({
  folder,
  children,
}: {
  folder: any;
  children: React.ReactNode;
}) => {
  return <AccordionItem border="none">{children}</AccordionItem>;
};

export const ChatDrawer = ({
  isOpen,
  onClose,
  sessions,
  handleNewChat,
  handleSelectSession,
  handleRename,
  handleDeleteSession,
  editingSessionId,
  sessionNameInput,
  setSessionNameInput,
  useColorModeValue,
  setEditingSessionId,
  handleStartRename,
  currentSession,
}: ChatDrawerProps) => {
  const { t } = useTranslation();
  const drawerBg = useColorModeValue("transparent", "transparent");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.100", "gray.600");
  const hoverColor = useColorModeValue("gray.800", "gray.200");

  const {
    folders,
    loadFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    addChatToFolder,
    removeChatFromFolder,
  } = useChatStore();

  const [newFolderName, setNewFolderName] = useState("");
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const {
    isOpen: isNewFolderOpen,
    onOpen: onNewFolderOpen,
    onClose: onNewFolderClose,
  } = useDisclosure();

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newFolderName.trim()) {
      await createFolder(newFolderName.trim());
      setNewFolderName("");
      onNewFolderClose();
    }
  };

  const handleUpdateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingFolderId && editingFolderName.trim()) {
      await updateFolder(editingFolderId, editingFolderName.trim());
      setEditingFolderId(null);
      setEditingFolderName("");
    }
  };

  const handleStartEditFolder = (folder: any) => {
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="left"
      onClose={onClose}
      size={{ base: "xs", md: "sm", lg: "md" }}
    >
      <DrawerOverlay />
      <DrawerContent
        bg={drawerBg}
        backdropFilter="blur(10px)"
        sx={{
          "& ::-webkit-scrollbar": {
            width: "8px",
          },
          "& ::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "& ::-webkit-scrollbar-thumb": {
            background: useColorModeValue(
              "rgba(0,0,0,0.1)",
              "rgba(255,255,255,0.1)"
            ),
            borderRadius: "8px",
          },
          "& ::-webkit-scrollbar-thumb:hover": {
            background: useColorModeValue(
              "rgba(0,0,0,0.2)",
              "rgba(255,255,255,0.2)"
            ),
          },
        }}
      >
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" pr={12}>
          <Flex justify="space-between" align="center">
            {t("navigation.chatHistory")}
            <Button
              leftIcon={<FiFolder />}
              size="sm"
              onClick={onNewFolderOpen}
              variant="ghost"
            >
              {t("common.folders.new")}
            </Button>
          </Flex>
        </DrawerHeader>
        <DrawerBody p={0}>
          <VStack w="full" spacing={0}>
            <Menu>
              <MenuButton
                as={Button}
                leftIcon={<FiPlus />}
                w="full"
                justifyContent="flex-start"
                rounded="none"
                colorScheme="brand"
                // variant="ghost"
                rightIcon={<FiMoreVertical />}
              >
                {t("chat.newSession")}
              </MenuButton>
              <MenuList>
                <MenuItem
                  icon={<FiMessageSquare />}
                  onClick={() => handleNewChat(ChatSessionType.STANDARD)}
                >
                  {t("chat.types.basic")}
                </MenuItem>
                <MenuItem
                  icon={<FiColumns />}
                  onClick={() =>
                    handleNewChat(ChatSessionType.COMPARATIVE_CHAT)
                  }
                >
                  {t("chat.types.multiChat")}
                </MenuItem>
                <MenuItem
                  icon={<FiImage />}
                  onClick={() => handleNewChat(ChatSessionType.IMAGE_CHAT)}
                >
                  {t("chat.types.imageGeneration")}
                </MenuItem>
                {process.env.NODE_ENV === "development" && (
                  <MenuItem
                    icon={<FiVideo />}
                    onClick={() => handleNewChat(ChatSessionType.VIDEO_CHAT)}
                  >
                    {t("chat.types.videoGeneration")}
                  </MenuItem>
                )}
                {process.env.NODE_ENV === "development" && (
                  <MenuItem
                    icon={<MdSlideshow />}
                    onClick={() => handleNewChat(ChatSessionType.PRESENTATION)}
                  >
                    {t("chat.types.presentation")} (Prototype)
                  </MenuItem>
                )}

                {process.env.NODE_ENV === "development" && (
                  <MenuItem
                    icon={<FiMusic />}
                    onClick={() => handleNewChat(ChatSessionType.AUDIO_CHAT)}
                  >
                    {t("chat.types.audioGeneration")} (Prototype)
                  </MenuItem>
                )}
                {process.env.NODE_ENV === "development" && (
                  <MenuItem
                    icon={<FiMusic />}
                    onClick={() => handleNewChat(ChatSessionType.MUSIC_CHAT)}
                  >
                    {t("chat.types.musicGeneration")} (Prototype)
                  </MenuItem>
                )}
                {process.env.NODE_ENV === "development" && (
                  <MenuItem
                    icon={<FiDatabase />}
                    onClick={() => handleNewChat(ChatSessionType.DATA_MINING)}
                  >
                    {t("chat.types.dataMining")} (Prototype)
                  </MenuItem>
                )}
                <MenuItem
                  icon={<FaCubes />}
                  onClick={() => handleNewChat(ChatSessionType.AGENT_CHAT)}
                >
                  {t("chat.types.agent")}
                </MenuItem>
              </MenuList>
            </Menu>
            {/* New Folder Form */}
            {isNewFolderOpen && (
              <Box p={4} w="full" borderBottom="1px" borderColor={borderColor}>
                <form onSubmit={handleCreateFolder}>
                  <HStack>
                    <Input
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder={t("common.folders.namePlaceholder")}
                      size="sm"
                    />
                    <Button type="submit" size="sm">
                      {t("common.folders.create")}
                    </Button>
                  </HStack>
                </form>
              </Box>
            )}
            {/* Folders */}
            <Accordion allowMultiple w="full">
              {folders.map((folder) => (
                <FolderItem key={folder.id} folder={folder}>
                  <AccordionButton
                    _hover={{ bg: hoverBg }}
                    borderBottom="1px"
                    borderColor={borderColor}
                  >
                    <Flex flex="1" align="center">
                      <Box color="gray.500" mr={3}>
                        <FiFolder />
                      </Box>
                      {editingFolderId === folder.id ? (
                        <form onSubmit={handleUpdateFolder}>
                          <Input
                            value={editingFolderName}
                            onChange={(e) =>
                              setEditingFolderName(e.target.value)
                            }
                            size="sm"
                            autoFocus
                            onBlur={() => setEditingFolderId(null)}
                          />
                        </form>
                      ) : (
                        <Text>{folder.name}</Text>
                      )}
                    </Flex>
                    <HStack spacing={1}>
                      <Tooltip
                        label={t("common.folders.rename")}
                        placement="top"
                      >
                        <IconButton
                          aria-label={t("common.folders.rename")}
                          icon={<FiEdit />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleStartEditFolder(folder)}
                        />
                      </Tooltip>
                      <Tooltip
                        label={t("common.folders.delete")}
                        placement="top"
                      >
                        <IconButton
                          aria-label={t("common.folders.delete")}
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => deleteFolder(folder.id)}
                        />
                      </Tooltip>
                      <AccordionIcon />
                    </HStack>
                  </AccordionButton>
                  <AccordionPanel p={0}>
                    <List>
                      {folder.chatSessions.map((chatSession) => (
                        <ChatItem
                          currentSession={currentSession}
                          key={chatSession.id}
                          chatSession={chatSession}
                          folders={folders}
                          handleSelectSession={handleSelectSession}
                          handleRename={handleRename}
                          handleDeleteSession={handleDeleteSession}
                          handleStartRename={handleStartRename}
                          editingSessionId={editingSessionId}
                          sessionNameInput={sessionNameInput}
                          setSessionNameInput={setSessionNameInput}
                          setEditingSessionId={setEditingSessionId}
                          removeChatFromFolder={removeChatFromFolder}
                          addChatToFolder={addChatToFolder}
                          useColorModeValue={useColorModeValue}
                          t={t}
                        />
                      ))}
                    </List>
                  </AccordionPanel>
                </FolderItem>
              ))}
            </Accordion>
            {/* Ungrouped Chats */}
            <List w="full">
              {sessions
                .filter(
                  (session) =>
                    !folders.some((folder) =>
                      folder.chatSessions.some(
                        (chatSession) => chatSession.id === session.id
                      )
                    )
                )
                .map((session) => (
                  <ChatItem
                    currentSession={currentSession}
                    key={session.id}
                    chatSession={session}
                    folders={folders}
                    handleSelectSession={handleSelectSession}
                    handleRename={handleRename}
                    handleDeleteSession={handleDeleteSession}
                    handleStartRename={handleStartRename}
                    editingSessionId={editingSessionId}
                    sessionNameInput={sessionNameInput}
                    setSessionNameInput={setSessionNameInput}
                    setEditingSessionId={setEditingSessionId}
                    removeChatFromFolder={removeChatFromFolder}
                    addChatToFolder={addChatToFolder}
                    useColorModeValue={useColorModeValue}
                    t={t}
                  />
                ))}
            </List>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
